import { useEffect, useRef, useState } from 'react';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { useSubscribe as _useSubscribe, useFind as _useFind, useTracker as _useTracker } from 'meteor/react-meteor-data';
import { MethodDeclaration } from '/imports/methods';
import { Publications } from '/imports/publications';
import { Subscriptions } from '/imports/subscriptions';
import { defer, isPublicPath, publicPaths } from '/imports/utils';

export const useTracker = _useTracker;

export const useLoggingIn = (deps: any[] = []) => {
  return _useTracker(() => Meteor.loggingIn(), deps);
};

export const useUser = () => {
  return useTracker(() => Meteor.user() || undefined, []);
};

export const useLoggedOutRedirect = () => {
  const pathname = window.location.pathname;
  const loggingIn = useLoggingIn();
  const loginTimeout = useRef<NodeJS.Timeout>(undefined);
  const userId = _useTracker(() => Meteor.userId(), []);
  const user = useUser();
  useEffect(() => {
    if (isPublicPath()) return;
    if (loggingIn) return;
    loginTimeout.current && clearTimeout(loginTimeout.current);
    loginTimeout.current = setTimeout(async () => {
      const user = await Meteor.userAsync();
      if (!loggingIn && !user?.active) {
        const redirect = publicPaths.includes(pathname) ? '/' : window.btoa(`${pathname}${window.location.search}`);
        window.location.href = `/login?r=${redirect}`;
        return;
      }
    }, 1000);
  }, [userId, loggingIn, user?.active]);
};

export const useFind = _useFind;

export const useFindOne = <T extends { _id: string }>(cursor: () => Mongo.Cursor<T>, deps: any[] = []) => {
  return useFind(cursor, deps)[0];
};

export const usePromise = <T>(promise: Promise<T>, deps: any[] = [promise]) => {
  const [resolved, setResolved] = useState<T>();
  const [error, setError] = useState<Error>();
  const [pending, setPending] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        setPending(true);
        setResolved(await promise);
        defer(() => setPending(false));
      }
      catch (error: any) {
        console.log(error);
        setError(error);
      }
      finally {
        defer(() => setPending(false));
      }
    })();
  }, deps);
  return [resolved, pending, error] as [T | undefined, boolean, Error | undefined];
};

export const useMethod = <A, R>(method: MethodDeclaration<A, R>, arg: A, deps: any[] = [], cond?: (deps: any[]) => boolean) => {
  // const [promise, setPromise] = useState<Promise<R | undefined>>(new Promise(() => {/* */}));
  // useEffect(() => {
  //   setPromise(cond && !cond(deps) ? Promise.resolve(undefined) : method.call(arg));
  // }, deps);
  // return usePromise(promise, [deps]);
  const result = useRef<R | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const error = useRef<Error | undefined>(undefined);
  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        result.current = cond && !cond(deps) ? undefined : await method.call(arg);
        error.current = undefined;
      }
      catch (error: any) {
        result.current = undefined;
        error.current = error;
      }
      finally {
        setLoading(false);
      }
    })();
  }, deps);
  return [result.current, loading, error.current] as [R | undefined, boolean, any | undefined];
};

export const useCollection = <T extends { _id?: string }>(docs: T[], deps: any[] = []) => {
  const [collection, setCollection] = useState<Mongo.Collection<T>>();
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<Error>();
  useEffect(() => {
    (async () => {
      try {
        const collection = new Mongo.Collection<T>(null);
        for (const doc of docs) {
          await collection.insertAsync(doc as any);
        }
        setInitializing(false);
        setCollection(collection);
      }
      catch (error: any) {
        console.log(error);
        setError(error);
      }
      finally {
        setInitializing(false);
      }
    })();
  }, deps);
  return [collection, initializing, error] as [Mongo.Collection<T>, boolean, Error | undefined];
};

export const useSubscribe = (subName: keyof typeof Publications | string, ...args: any[]) => {
  return _useSubscribe(subName, ...args);
};

export const useSubscription = (subName: keyof typeof Publications | string, ...args: any[]) => {
  return _useSubscribe(subName, ...args)();
};

export const useSubscribed = (name: string, deps: any[] = []) => {
  const sub = Subscriptions[name];
  if (!sub) throw new Error(`useSubscribed: ${name} not registered`);
  const [loading, setLoading] = useState(sub());
  useEffect(() => { setLoading(sub()); }, [sub(), ...deps]);
  return loading;
};

export const usePrevious = <T>(value: T) => {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};